import { Brand, Item, Supplier, Unit } from '$models'
import {
  brandService,
  itemService,
  supplierService,
  unitService,
} from '$services'
import { exceptionMessageHandler } from '$utils'
import { Dictionary } from 'lodash'
import { StateCreator } from 'zustand'
import { arrayToDictionary } from './invoices/helper'

interface SharedStockState {
  suppliers: Dictionary<Supplier>
  items: Dictionary<Item>
  brands: Dictionary<Brand>
  units: Dictionary<Unit>
}

export interface SharedStockSlice extends SharedStockState {
  /**
   * Get all suppliers
   */
  getSuppliers: () => Promise<void>

  /**
   * Get all items
   */
  getItems: () => Promise<Dictionary<Item>>

  /**
   * Get all brands
   */
  getBrands: () => Promise<Dictionary<Brand>>

  /**
   * Get all units
   */
  getUnits: () => Promise<Dictionary<Unit>>

  /**
   * Reset the Shared Stock Slice to its inital State
   */
  resetSharedStockSlice: () => void
}

const initialState: SharedStockState = {
  suppliers: {},
  items: {},
  brands: {},
  units: {},
}

export const createSharedStockSlice: StateCreator<
  SharedStockSlice,
  [],
  [],
  SharedStockSlice
> = (set, get) => ({
  ...initialState,

  resetSharedStockSlice: () => {
    set(initialState)
  },

  getSuppliers: async (): Promise<void> => {
    if (Object.keys(get().suppliers).length > 0) return
    try {
      const suppliers = await supplierService.getAllSuppliers()
      const supplierDict = arrayToDictionary<Supplier>('id', suppliers)
      set(() => ({
        suppliers: supplierDict,
      }))
    } catch (error) {
      set(() => ({
        suppliers: {},
      }))
      exceptionMessageHandler(error, 'Failed to retrieve suppliers')
    }
  },

  getItems: async (): Promise<Dictionary<Item>> => {
    if (Object.keys(get().items).length > 0) return get().items
    try {
      const items = await itemService.getAllItems()
      const itemsDict = arrayToDictionary<Item>('id', items)
      set(() => ({
        items: itemsDict,
      }))
      return itemsDict
    } catch (error) {
      set(() => ({
        items: {},
      }))
      exceptionMessageHandler(error, 'Failed to retrieve items')
      return {}
    }
  },

  getBrands: async (): Promise<Dictionary<Brand>> => {
    if (Object.keys(get().brands).length > 0) return get().brands
    try {
      const brands = await brandService.getAllBrands()
      const brandsDict = arrayToDictionary<Brand>('id', brands)
      set(() => ({
        brands: brandsDict,
      }))
      return brandsDict
    } catch (error) {
      set(() => ({
        brands: {},
      }))
      exceptionMessageHandler(error, 'Failed to retrieve brands')
      return {}
    }
  },

  getUnits: async (): Promise<Dictionary<Unit>> => {
    if (Object.keys(get().units).length > 0) return get().units
    try {
      const units = await unitService.getAllUnits()
      const unitsDict = arrayToDictionary<Unit>('id', units)
      set(() => ({
        units: unitsDict,
      }))
      return unitsDict
    } catch (error) {
      set(() => ({
        units: {},
      }))
      exceptionMessageHandler(error, 'Failed to retrieve units')
      return {}
    }
  },
})

export default createSharedStockSlice
